import { useMutation } from '@tanstack/vue-query'
import { useApiError } from '~/composables/useErrors'
import { useStatefulToast } from '~/composables/useStatefulToast'
import type { MutationOptions } from '@tanstack/query-core'
import type { QueryClient } from '@tanstack/vue-query'
import { escapeHtml } from '~/utils/htmlSafety'

interface ToastMessages<TData> {
  pending?: string
  success?: string | ((data: TData) => string)
  error?: string
}

export function generateErrorHtmlList(errorData: Record<string, string[]>): string {
  let html = '<ul>'
  for (const [_key, messages] of Object.entries(errorData)) {
    html += messages.map((message) => `<li>${escapeHtml(message)}</li>`).join('')
  }
  html += '</ul>'
  return html
}

export function useMutationWithToast<TData, TVariables>(
  mutationOptions: MutationOptions<TData, Error, TVariables>,
  queryClient?: QueryClient,
  toastMessages: ToastMessages<TData> = {},
) {
  const { getApiErrorMessage } = useApiError()
  const toast = useStatefulToast()

  return useMutation(
    {
      ...mutationOptions,
      onMutate: async (variables, context) => {
        toast.pending(toastMessages.pending || 'Action is pending...')

        // Call the user's onMutate if it exists
        if (mutationOptions?.onMutate) {
          await mutationOptions.onMutate(variables, context)
        }

        return
      },
      onSuccess: async (data, variables, result, context) => {
        toast.success(
          typeof toastMessages.success === 'function'
            ? toastMessages.success(data)
            : toastMessages.success || 'Action successful!',
        )

        if (mutationOptions?.onSuccess) {
          await mutationOptions.onSuccess(data, variables, result, context)
        }
      },
      throwOnError: false,
      onError: (err, variables, result, context) => {
        let errorMessage: string | null = null
        const error = err as Error & {
          status?: number
          statusCode?: number
          data?: Record<string, string[]>
          result?: {
            details?: string
            error?: string
          }
        }
        const code = error.status || error.statusCode
        if (code && code < 500 && error.data) {
          try {
            errorMessage = getApiErrorMessage(error.data) || generateErrorHtmlList(error.data)
          } catch (e) {
            console.error('failed to generate html list', e)
          }
        }

        if (!errorMessage) {
          errorMessage = error.result?.details || error.message || null
        }

        toast.error({
          message: toastMessages.error || 'Action failed!',
          details: errorMessage || undefined,
        })

        // Call the user's onError if it exists
        if (mutationOptions?.onError) {
          mutationOptions.onError(error, variables, result, context)
        }
      },
    },
    queryClient,
  )
}
